import type { DiscountMapper } from '@domains/billing-settings/priv/CustomerDiscount/api/mappers'
import {
  type CreateDiscountRequest,
  type Discount,
  DiscountTypeDiscriminator,
  type PerUnitDiscount as PerUnitDiscountSpec,
} from '@mynt/backoffice-api-spec/discounts'
import { type CreateDiscount, DiscountModifier, type Discounts, DiscountType, type PerUnitDiscount } from '../../api/api'

type PerUnitDiscountDto = Discount & {
  discountType: PerUnitDiscountSpec
}

export class PerUnitDiscountDtoMapper implements DiscountMapper {
  discountTypeLabel = 'Per unit'
  supports(discount: Discount): discount is PerUnitDiscountDto {
    return discount?.discountType?.type === DiscountTypeDiscriminator.PER_UNIT_DISCOUNT
  }

  supportsType(discountType: string): boolean {
    return discountType === DiscountTypeDiscriminator.PER_UNIT_DISCOUNT
  }

  supportsCreateDiscount(request: CreateDiscount): request is CreateDiscount<PerUnitDiscount> {
    return request.discountType.type === DiscountType.PerUnitDiscount
  }

  asDiscounts(discount: Discount): Discounts {
    if (!this.supports(discount)) {
      throw new Error('Unsupported discount type')
    }

    switch (discount.discountType.modifierType) {
      case DiscountModifier.ABSOLUTE: {
        return {
          adornment: discount.discountType.absoluteAmount?.currency!,
          expirationDate: discount.expirationDate ? new Date(discount.expirationDate) : undefined,
          type: DiscountType.PerUnitDiscount,
          value: discount.discountType.absoluteAmount?.value!,
          modifier: DiscountModifier.ABSOLUTE,
        }
      }

      case DiscountModifier.PERCENTAGE: {
        return {
          type: DiscountType.PerUnitDiscount,
          adornment: '%',
          value: discount.discountType.percentageValue!,
          expirationDate: discount.expirationDate ? new Date(discount.expirationDate) : undefined,
          modifier: DiscountModifier.PERCENTAGE,
        }
      }
    }

    throw new Error(`Unsupported discount modifier: ${discount.discountType.modifierType}`)
  }

  asCreateDiscountRequest(request: CreateDiscount): CreateDiscountRequest {
    if (!this.supportsCreateDiscount(request)) {
      throw new Error('Unsupported discount type')
    }

    switch (request.discountType.modifier) {
      case DiscountModifier.PERCENTAGE: {
        return {
          discountType: {
            type: DiscountTypeDiscriminator.PER_UNIT_DISCOUNT,
            modifierType: request.discountType.modifier,
            percentageValue: request.discountType.percentageValue,
          },
          expirationDate: request.expirationDate,
        }
      }

      case DiscountModifier.ABSOLUTE: {
        return {
          discountType: {
            type: DiscountTypeDiscriminator.PER_UNIT_DISCOUNT,
            modifierType: request.discountType.modifier,
            absoluteAmount: {
              currency: request.discountType.absoluteAmount?.currency!,
              value: request.discountType.absoluteAmount?.value!,
            },
          },
          expirationDate: request.expirationDate,
        }
      }
    }

    // @ts-expect-error it is technically reachable
    throw new Error(`Unsupported discount modifier: ${request.discountType.modifier}`)
  }
}
